import React from 'react';
import { PageWrapper } from '@components/layout/page-wrapper';
import { GlassCard } from '@components/primitives/glass-card';
import styled from 'styled-components';
import { spacing } from '@config/spacing';
import { typography } from '@config/typography';
import { colors } from '@config/seafoam_colors';

const PrivacyContainer = styled.div`
  padding: ${spacing['4xl']} ${spacing.xl};
  max-width: 900px;
  margin: 0 auto;
`;

const PrivacyCard = styled(GlassCard)`
  background: ${colors.white};
  padding: ${spacing['4xl']};
  gap: ${spacing.xl};

  @media (max-width: 768px) {
    padding: ${spacing['2xl']};
  }
`;

const Title = styled.h1`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize['4xl']};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text};
  margin: 0 0 ${spacing.md} 0;
  text-align: center;
`;

const LastUpdated = styled.p`
  text-align: center;
  color: ${colors.textSecondary};
  font-size: ${typography.fontSize.sm};
  margin: 0 0 ${spacing['2xl']} 0;
`;

const Section = styled.section`
  margin-bottom: ${spacing['2xl']};
`;

const SectionTitle = styled.h2`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize['2xl']};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text};
  margin: ${spacing.xl} 0 ${spacing.md} 0;
`;

const SubsectionTitle = styled.h3`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text};
  margin: ${spacing.lg} 0 ${spacing.sm} 0;
`;

const Paragraph = styled.p`
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  color: ${colors.text};
  line-height: ${typography.lineHeight.bodyRelaxed};
  margin: ${spacing.md} 0;
`;

const List = styled.ul`
  margin: ${spacing.md} 0;
  padding-left: ${spacing.xl};
  
  li {
    font-family: ${typography.fontFamily.body};
    font-size: ${typography.fontSize.base};
    color: ${colors.text};
    line-height: ${typography.lineHeight.bodyRelaxed};
    margin: ${spacing.sm} 0;
  }
`;

const ContactInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${spacing.md};
  padding: ${spacing.lg};
  margin: ${spacing.lg} 0;
`;

const Link = styled.a`
  color: ${colors.primary};
  text-decoration: underline;

  &:hover {
    color: ${colors.primaryDark};
  }
`;

/**
 * Privacy Policy Page
 * Displays the complete privacy policy for HomeCEO
 */
export const PrivacyPolicyPage: React.FC = () => {
  return (
    <PageWrapper contained>
      <PrivacyContainer>
        <PrivacyCard>
          <Title>PRIVACY POLICY</Title>
          <LastUpdated>Last updated October 14, 2026</LastUpdated>

          <Paragraph>
            This Privacy Notice for <strong>Lat Long Labs LLC</strong> (doing business as <strong>The Home CEO</strong>)
            (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), describes how and why we might access, collect, store, use,
            and/or share (&quot;process&quot;) your personal information when you use our services (&quot;Services&quot;),
            including when you:
          </Paragraph>

          <List>
            <li>Visit our website at homeceo.ai or any website of ours that links to this Privacy Notice</li>
            <li>Download and use our mobile application (The Home CEO), or any other application of ours that links to this Privacy Notice</li>
            <li>Engage with us in other related ways, including any sales, marketing, or events</li>
          </List>

          <Paragraph>
            <strong>Questions or concerns?</strong> Reading this Privacy Notice will help you understand your 
            privacy rights and choices. We are responsible for making decisions about how your personal 
            information is processed. If you do not agree with our policies and practices, please do not use 
            our Services. If you still have any questions or concerns, please contact us at{' '}
            <Link href="mailto:support@homeceo.ai">support@homeceo.ai</Link>.
          </Paragraph>

          <Section>
            <SectionTitle>SUMMARY OF KEY POINTS</SectionTitle>
            <Paragraph>
              This summary provides key points from our Privacy Notice, but you can find out more details 
              about any of these topics by using our table of contents below to find the section you are looking for.
            </Paragraph>

            <Paragraph>
              <strong>What personal information do we process?</strong> When you visit, use, or navigate our 
              Services, we may process personal information depending on how you interact with us and the 
              Services, the choices you make, and the products and features you use.
            </Paragraph>

            <Paragraph>
              <strong>Do we process any sensitive personal information?</strong> Some of the information may be
              considered &quot;special&quot; or &quot;sensitive&quot; in certain jurisdictions, for example your racial or ethnic
              origins, sexual orientation, and religious beliefs. We may process sensitive personal information
              when necessary with your consent or as otherwise permitted by applicable law.
            </Paragraph>

            <Paragraph>
              <strong>Do we collect any information from third parties?</strong> We do not collect any information 
              from third parties.
            </Paragraph>

            <Paragraph>
              <strong>How do we process your information?</strong> We process your information to provide, improve, 
              and administer our Services, communicate with you, for security and fraud prevention, and to comply 
              with law. We may also process your information for other purposes with your consent. We process your 
              information only when we have a valid legal reason to do so.
            </Paragraph>

            <Paragraph>
              <strong>In what situations and with which parties do we share personal information?</strong> We may 
              share information in specific situations and with specific third parties.
            </Paragraph>

            <Paragraph>
              <strong>How do we keep your information safe?</strong> We have adequate organizational and technical 
              processes and procedures in place to protect your personal information. However, no electronic 
              transmission over the internet or information storage technology can be guaranteed to be 100% secure, 
              so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties 
              will not be able to defeat our security and improperly collect, access, steal, or modify your information.
            </Paragraph>

            <Paragraph>
              <strong>What are your rights?</strong> Depending on where you are located geographically, the 
              applicable privacy law may mean you have certain rights regarding your personal information.
            </Paragraph>

            <Paragraph>
              <strong>How do you exercise your rights?</strong> The easiest way to exercise your rights is by
              contacting us at <Link href="/legal/contact-us">https://homeceo.ai/legal/contact-us</Link>.
              We will consider and act upon any request in accordance with applicable data
              protection laws.
            </Paragraph>

            <Paragraph>
              <strong>Global Privacy Control (GPC):</strong> We honor the Global Privacy Control (GPC) opt-out 
              signal on your browser. If your browser sends a GPC signal, we will automatically apply the most 
              restrictive privacy settings and disable optional data collection.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>TABLE OF CONTENTS</SectionTitle>
            <List>
              <li>1. WHAT INFORMATION DO WE COLLECT?</li>
              <li>2. HOW DO WE PROCESS YOUR INFORMATION?</li>
              <li>3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?</li>
              <li>4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
              <li>5. WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?</li>
              <li>6. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
              <li>7. DO WE OFFER ARTIFICIAL INTELLIGENCE-BASED PRODUCTS?</li>
              <li>8. HOW DO WE HANDLE YOUR SOCIAL LOGINS?</li>
              <li>9. HOW LONG DO WE KEEP YOUR INFORMATION?</li>
              <li>10. HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
              <li>11. DO WE COLLECT INFORMATION FROM MINORS?</li>
              <li>12. WHAT ARE YOUR PRIVACY RIGHTS?</li>
              <li>13. CONTROLS FOR DO-NOT-TRACK FEATURES</li>
              <li>14. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</li>
              <li>15. DO OTHER REGIONS HAVE SPECIFIC PRIVACY RIGHTS?</li>
              <li>16. DO WE MAKE UPDATES TO THIS NOTICE?</li>
              <li>17. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
              <li>18. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>1. WHAT INFORMATION DO WE COLLECT?</SectionTitle>
            
            <SubsectionTitle>Personal information you disclose to us</SubsectionTitle>
            <Paragraph>
              <strong>In Short:</strong> We collect personal information that you provide to us.
            </Paragraph>
            <Paragraph>
              We collect personal information that you voluntarily provide to us when you register on the Services, 
              express an interest in obtaining information about us or our products and Services, when you participate 
              in activities on the Services, or otherwise when you contact us.
            </Paragraph>
            <Paragraph>
              <strong>Personal Information Provided by You.</strong> The personal information that we collect depends 
              on the context of your interactions with us and the Services, the choices you make, and the products and 
              features you use. The personal information we collect may include the following:
            </Paragraph>
            <List>
              <li>names</li>
              <li>phone numbers</li>
              <li>email addresses</li>
              <li>mailing addresses</li>
              <li>job titles</li>
              <li>usernames</li>
              <li>passwords</li>
              <li>contact preferences</li>
              <li>contact or authentication data</li>
              <li>billing addresses</li>
              <li>any information provided to chatbots</li>
            </List>

            <Paragraph>
              <strong>Sensitive Information.</strong> When necessary, with your consent or as otherwise permitted by 
              applicable law, we process the following categories of sensitive information:
            </Paragraph>
            <List>
              <li>financial data</li>
              <li>any information provided to chatbots</li>
            </List>

            <Paragraph>
              <strong>Payment Data.</strong> We may collect data necessary to process your payment if you choose to 
              make purchases, such as your payment instrument number, and the security code associated with your 
              payment instrument. All payment data is handled and stored by Stripe. You may find their privacy notice 
              at: <Link href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</Link>.
            </Paragraph>

            <Paragraph>
              <strong>Social Media Login Data.</strong> We may provide you with the option to register with us using
              your existing social media account details, like your Google, Facebook, or Apple account. If you choose
              to register in this way, we will collect certain profile information about you from the social media
              provider, as described in the section called &quot;HOW DO WE HANDLE YOUR SOCIAL LOGINS?&quot; below.
            </Paragraph>

            <Paragraph>
              <strong>Application Data.</strong> If you use our application(s), we also may collect the following 
              information if you choose to provide us with access or permission:
            </Paragraph>
            <List>
              <li><strong>Mobile Device Access.</strong> We may request access or permission to certain features from
              your mobile device, including your mobile device&apos;s calendar, contacts, reminders, social media accounts,
              and other features. If you wish to change our access or permissions, you may do so in your device&apos;s settings.</li>
              <li><strong>Mobile Device Data.</strong> We automatically collect device information (such as your mobile
              device ID, model, and manufacturer), operating system, version information and system configuration
              information, device and application identification numbers, browser type and version, hardware model
              Internet service provider and/or mobile carrier, and Internet Protocol (IP) address (or proxy server).</li>
              <li><strong>Push Notifications.</strong> We may request to send you push notifications regarding your
              account or certain features of the application(s). If you wish to opt out from receiving these types of
              communications, you may turn them off in your device&apos;s settings.</li>
            </List>

            <Paragraph>
              All personal information that you provide to us must be true, complete, and accurate, and you must 
              notify us of any changes to such personal information.
            </Paragraph>

            <SubsectionTitle>Information automatically collected</SubsectionTitle>
            <Paragraph>
              <strong>In Short:</strong> Some information — such as your Internet Protocol (IP) address and/or 
              browser and device characteristics — is collected automatically when you visit our Services.
            </Paragraph>
            <Paragraph>
              We automatically collect certain information when you visit, use, or navigate the Services. This 
              information does not reveal your specific identity (like your name or contact information) but may 
              include device and usage information, such as your IP address, browser and device characteristics, 
              operating system, language preferences, referring URLs, device name, country, location, information 
              about how and when you use our Services, and other technical information.
            </Paragraph>

            <SubsectionTitle>Google API</SubsectionTitle>
            <Paragraph>
              Our use of information received from Google APIs will adhere to Google API Services User Data Policy, 
              including the Limited Use requirements.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>2. HOW DO WE PROCESS YOUR INFORMATION?</SectionTitle>
            <Paragraph>
              <strong>In Short:</strong> We process your information to provide, improve, and administer our Services, 
              communicate with you, for security and fraud prevention, and to comply with law.
            </Paragraph>
            <Paragraph>
              We process your personal information for a variety of reasons, depending on how you interact with our 
              Services, including:
            </Paragraph>
            <List>
              <li>To facilitate account creation and authentication and otherwise manage user accounts</li>
              <li>To deliver and facilitate delivery of services to the user</li>
              <li>To respond to user inquiries/offer support to users</li>
              <li>To send administrative information to you</li>
              <li>To fulfill and manage your orders</li>
              <li>To save or protect an individual&apos;s vital interest</li>
              <li>To provide customized research</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?</SectionTitle>
            <Paragraph>
              <strong>In Short:</strong> We only process your personal information when we believe it is necessary 
              and we have a valid legal reason (i.e., legal basis) to do so under applicable law, like with your 
              consent, to comply with laws, to provide you with services to enter into or fulfill our contractual 
              obligations, to protect your rights, or to fulfill our legitimate business interests.
            </Paragraph>
            
            <SubsectionTitle>If you are located in the EU or UK, this section applies to you.</SubsectionTitle>
            <Paragraph>
              The General Data Protection Regulation (GDPR) and UK GDPR require us to explain the valid legal bases 
              we rely on in order to process your personal information. As such, we may rely on the following legal 
              bases to process your personal information:
            </Paragraph>
            <List>
              <li><strong>Consent.</strong> We may process your information if you have given us permission to use 
              your personal information for a specific purpose. You can withdraw your consent at any time.</li>
              <li><strong>Performance of a Contract.</strong> We may process your personal information when we believe 
              it is necessary to fulfill our contractual obligations to you.</li>
              <li><strong>Legal Obligations.</strong> We may process your information where we believe it is necessary 
              for compliance with our legal obligations.</li>
              <li><strong>Vital Interests.</strong> We may process your information where we believe it is necessary 
              to protect your vital interests or the vital interests of a third party.</li>
            </List>

            <SubsectionTitle>If you are located in Canada, this section applies to you.</SubsectionTitle>
            <Paragraph>
              We may process your information if you have given us specific permission (i.e., express consent) to use 
              your personal information for a specific purpose, or in situations where your permission can be inferred 
              (i.e., implied consent). You can withdraw your consent at any time.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</SectionTitle>
            <Paragraph>
              <strong>In Short:</strong> We may share information in specific situations described in this section 
              and/or with the following third parties.
            </Paragraph>
            <Paragraph>
              We may need to share your personal information in the following situations:
            </Paragraph>
            <List>
              <li><strong>Business Transfers.</strong> We may share or transfer your information in connection with, 
              or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a 
              portion of our business to another company.</li>
            </List>
            <Paragraph>
              <strong>SMS Opt-In Data.</strong> We do not sell or share your SMS opt-in data or personal information
              with third parties for marketing purposes.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>5. WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?</SectionTitle>
            <Paragraph>
              <strong>In Short:</strong> We are not responsible for the safety of any information that you share with 
              third parties that we may link to or who advertise on our Services, but are not affiliated with our Services.
            </Paragraph>
            <Paragraph>
              The Services may link to third-party websites, online services, or mobile applications and/or contain 
              advertisements from third parties that are not affiliated with us. We cannot guarantee the safety and 
              privacy of data you provide to any third-party websites. Any data collected by third parties is not 
              covered by this Privacy Notice. We are not responsible for the content or privacy and security practices 
              and policies of any third parties.
            </Paragraph>
          </Section>

          <Section id="cookies">
            <SectionTitle>6. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</SectionTitle>
            <Paragraph>
              <strong>In Short:</strong> We may use cookies and other tracking technologies to collect and store your information.
            </Paragraph>
            <Paragraph>
              We may use cookies and similar tracking technologies (like web beacons and pixels) to gather information 
              when you interact with our Services. Some online tracking technologies help us maintain the security of 
              our Services and your account, prevent crashes, fix bugs, save your preferences, and assist with basic 
              site functions.
            </Paragraph>

            <SubsectionTitle>Google Analytics</SubsectionTitle>
            <Paragraph>
              We may share your information with Google Analytics to track and analyze the use of the Services. To opt 
              out of being tracked by Google Analytics across the Services, visit{' '}
              <Link href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
                https://tools.google.com/dlpage/gaoptout
              </Link>. For more information on the privacy practices of Google, please visit the{' '}
              <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                Google Privacy & Terms page
              </Link>.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>7. DO WE OFFER ARTIFICIAL INTELLIGENCE-BASED PRODUCTS?</SectionTitle>
            <Paragraph>
              <strong>In Short:</strong> We offer products, features, or tools powered by artificial intelligence, 
              machine learning, or similar technologies.
            </Paragraph>
            <Paragraph>
              As part of our Services, we offer products, features, or tools powered by artificial intelligence,
              machine learning, or similar technologies (collectively, &quot;AI Products&quot;). These tools are designed to
              enhance your experience and provide you with innovative solutions.
            </Paragraph>
            <Paragraph>
              We provide the AI Products through third-party service providers (&quot;AI Service Providers&quot;), including
              Anthropic, Google Cloud AI, and OpenAI. Your input, output, and personal information will be shared with
              and processed by these AI Service Providers to enable your use of our AI Products.
            </Paragraph>
            <Paragraph>
              Our AI Products are designed for the following functions:
            </Paragraph>
            <List>
              <li>AI applications</li>
              <li>AI document generation</li>
              <li>AI research</li>
              <li>AI search</li>
              <li>AI predictive analytics</li>
              <li>Machine learning models</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>8. HOW DO WE HANDLE YOUR SOCIAL LOGINS?</SectionTitle>
            <Paragraph>
              <strong>In Short:</strong> If you choose to register or log in to our Services using a social media 
              account, we may have access to certain information about you.
            </Paragraph>
            <Paragraph>
              Our Services offer you the ability to register and log in using your third-party social media account 
              details (like your Google, Facebook, or Apple logins). Where you choose to do this, we will receive 
              certain profile information about you from your social media provider. The profile information we receive 
              may vary depending on the social media provider concerned, but will often include your name, email 
              address, friends list, and profile picture, as well as other information you choose to make public on 
              such a social media platform.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>9. HOW LONG DO WE KEEP YOUR INFORMATION?</SectionTitle>
            <Paragraph>
              <strong>In Short:</strong> We keep your information for as long as necessary to fulfill the purposes 
              outlined in this Privacy Notice unless otherwise required by law.
            </Paragraph>
            <Paragraph>
              We will only keep your personal information for as long as it is necessary for the purposes set out in
              this Privacy Notice, unless a longer retention period is required or permitted by law. No purpose in this
              notice will require us keeping your personal information for longer than one (1) month past the
              termination of the user&apos;s account.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>10. HOW DO WE KEEP YOUR INFORMATION SAFE?</SectionTitle>
            <Paragraph>
              <strong>In Short:</strong> We aim to protect your personal information through a system of organizational 
              and technical security measures.
            </Paragraph>
            <Paragraph>
              We have implemented appropriate and reasonable technical and organizational security measures designed to 
              protect the security of any personal information we process. However, despite our safeguards and efforts 
              to secure your information, no electronic transmission over the Internet or information storage technology 
              can be guaranteed to be 100% secure.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>11. DO WE COLLECT INFORMATION FROM MINORS?</SectionTitle>
            <Paragraph>
              <strong>In Short:</strong> We do not knowingly collect data from or market to children under 18 years of 
              age or the equivalent age as specified by law in your jurisdiction.
            </Paragraph>
            <Paragraph>
              We do not knowingly collect, solicit data from, or market to children under 18 years of age, nor do we
              knowingly sell such personal information. By using the Services, you represent that you are at least 18
              or that you are the parent or guardian of such a minor and consent to such minor dependent&apos;s use of the
              Services. If you become aware of any data we may have collected from children under age 18, please
              contact us at <Link href="mailto:support@homeceo.ai">support@homeceo.ai</Link>.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>12. WHAT ARE YOUR PRIVACY RIGHTS?</SectionTitle>
            <Paragraph>
              <strong>In Short:</strong> Depending on your state of residence in the US or in some regions, such as 
              the European Economic Area (EEA), United Kingdom (UK), Switzerland, and Canada, you have rights that 
              allow you greater access to and control over your personal information.
            </Paragraph>
            <Paragraph>
              In some regions (like the EEA, UK, Switzerland, and Canada), you have certain rights under applicable 
              data protection laws. These may include the right (i) to request access and obtain a copy of your 
              personal information, (ii) to request rectification or erasure; (iii) to restrict the processing of your 
              personal information; (iv) if applicable, to data portability; and (v) not to be subject to automated 
              decision-making.
            </Paragraph>

            <SubsectionTitle>Withdrawing your consent</SubsectionTitle>
            <Paragraph>
              If we are relying on your consent to process your personal information, you have the right to withdraw 
              your consent at any time. You can withdraw your consent at any time by contacting us or updating your 
              preferences.
            </Paragraph>

            <SubsectionTitle>Opting out of marketing and promotional communications</SubsectionTitle>
            <Paragraph>
              You can unsubscribe from our marketing and promotional communications at any time by clicking on the
              unsubscribe link in the emails that we send, replying &quot;STOP&quot; or &quot;UNSUBSCRIBE&quot; to the SMS messages that
              we send, or by contacting us.
            </Paragraph>

            <SubsectionTitle>Account Information</SubsectionTitle>
            <Paragraph>
              If you would at any time like to review or change the information in your account or terminate your 
              account, you can log in to your account settings and update your user account.
            </Paragraph>
            <Paragraph>
              Upon your request to terminate your account, we will deactivate or delete your account and information 
              from our active databases. However, we may retain some information in our files to prevent fraud, 
              troubleshoot problems, assist with any investigations, enforce our legal terms and/or comply with 
              applicable legal requirements.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>13. CONTROLS FOR DO-NOT-TRACK FEATURES</SectionTitle>
            <Paragraph>
              Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track (&quot;DNT&quot;)
              feature or setting you can activate to signal your privacy preference not to have data about your online
              browsing activities monitored and collected.
            </Paragraph>
            <Paragraph>
              <strong>Global Privacy Control (GPC):</strong> We honor the Global Privacy Control (GPC) opt-out signal. 
              If your browser sends a GPC signal, we will automatically disable non-essential cookies, turn off 
              analytics tracking, and apply the most restrictive privacy settings. Learn more at{' '}
              <Link href="https://globalprivacycontrol.org/" target="_blank" rel="noopener noreferrer">
                https://globalprivacycontrol.org/
              </Link>.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>14. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</SectionTitle>
            <Paragraph>
              <strong>In Short:</strong> If you are a resident of California, Colorado, Connecticut, Delaware, Florida, 
              Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode 
              Island, Tennessee, Texas, Utah, or Virginia, you may have the right to request access to and receive 
              details about the personal information we maintain about you and how we have processed it, correct 
              inaccuracies, get a copy of, or delete your personal information.
            </Paragraph>

            <SubsectionTitle>Your Rights</SubsectionTitle>
            <List>
              <li>Right to know whether or not we are processing your personal data</li>
              <li>Right to access your personal data</li>
              <li>Right to correct inaccuracies in your personal data</li>
              <li>Right to request the deletion of your personal data</li>
              <li>Right to obtain a copy of the personal data you previously shared with us</li>
              <li>Right to non-discrimination for exercising your rights</li>
              <li>Right to opt out of the processing of your personal data if it is used for targeted advertising</li>
            </List>

            <SubsectionTitle>How to Exercise Your Rights</SubsectionTitle>
            <Paragraph>
              To exercise these rights, you can contact us by visiting{' '}
              <Link href="/legal/contact-us">https://homeceo.ai/legal/contact-us</Link>, or by referring to
              the contact details at the bottom of this document.
            </Paragraph>
            <Paragraph>
              We will honor your opt-out preferences if you enact the Global Privacy Control (GPC) opt-out signal on 
              your browser.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>15. DO OTHER REGIONS HAVE SPECIFIC PRIVACY RIGHTS?</SectionTitle>
            <Paragraph>
              <strong>In Short:</strong> You may have additional rights based on the country you reside in.
            </Paragraph>
            
            <SubsectionTitle>Australia and New Zealand</SubsectionTitle>
            <Paragraph>
              We collect and process your personal information under the obligations and conditions set by Australia&apos;s
              Privacy Act 1988 and New Zealand&apos;s Privacy Act 2020.
            </Paragraph>

            <SubsectionTitle>Republic of South Africa</SubsectionTitle>
            <Paragraph>
              At any time, you have the right to request access to or correction of your personal information. You can 
              make such a request by contacting us.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>16. DO WE MAKE UPDATES TO THIS NOTICE?</SectionTitle>
            <Paragraph>
              <strong>In Short:</strong> Yes, we will update this notice as necessary to stay compliant with relevant laws.
            </Paragraph>
            <Paragraph>
              We may update this Privacy Notice from time to time. The updated version will be indicated by an updated
              &quot;Last updated&quot; date at the top of this Privacy Notice. If we make material changes to this Privacy Notice,
              we may notify you either by prominently posting a notice of such changes or by directly sending you a
              notification.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>17. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</SectionTitle>
            <Paragraph>
              If you have questions or comments about this notice, you may email us at{' '}
              <Link href="mailto:support@homeceo.ai">support@homeceo.ai</Link> or contact us by post at:
            </Paragraph>
            <ContactInfo>
              <strong>Lat Long Labs LLC</strong><br />
              3159 Stelling Dr<br />
              Palo Alto, CA 94303<br />
              United States
            </ContactInfo>
          </Section>

          <Section>
            <SectionTitle>18. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</SectionTitle>
            <Paragraph>
              Based on the applicable laws of your country or state of residence in the US, you may have the right to 
              request access to the personal information we collect from you, details about how we have processed it, 
              correct inaccuracies, or delete your personal information. You may also have the right to withdraw your 
              consent to our processing of your personal information.
            </Paragraph>
            <Paragraph>
              To request to review, update, or delete your personal information, please contact us at:{' '}
              <Link href="/legal/contact-us">https://homeceo.ai/legal/contact-us</Link>.
            </Paragraph>
          </Section>
        </PrivacyCard>
      </PrivacyContainer>
    </PageWrapper>
  );
};

